// A geometria do gráfico da história de um exame — sem React, para testar.
//
// Eixo do tempo de verdade (a distância entre os pontos é a distância entre as
// coletas: três exames numa semana e um no mês seguinte não ficam igualmente
// espaçados), a faixa normal em verde por trás, e marcas verticais onde algo
// começou (um remédio) ou aconteceu (uma intercorrência).

export interface GeometriaDoHistorico {
  pontos: { x: number; y: number }[];
  faixa: { y: number; h: number } | null;
  yTicks: { y: number; valor: number }[];
  xTicks: { x: number; em: string }[];
  /** x de cada marca, ou null se cai fora do período medido. */
  marcas: (number | null)[];
  area: { x0: number; x1: number; y0: number; y1: number };
}

export const MARGEM = { esq: 40, dir: 12, cima: 14, baixo: 24 };

export function geometriaDoHistorico(
  serie: { valor: number; em: string }[],
  referencia: { min: number; max: number } | null,
  marcas: { em: string }[],
  largura: number,
  altura: number,
): GeometriaDoHistorico {
  const x0 = MARGEM.esq, x1 = largura - MARGEM.dir, y0 = MARGEM.cima, y1 = altura - MARGEM.baixo;
  const area = { x0, x1, y0, y1 };
  if (serie.length === 0) return { pontos: [], faixa: null, yTicks: [], xTicks: [], marcas: marcas.map(() => null), area };

  const tempos = serie.map((p) => Date.parse(p.em));
  let t0 = Math.min(...tempos), t1 = Math.max(...tempos);
  if (t1 - t0 < 86_400_000) { t0 -= 43_200_000; t1 += 43_200_000; } // um ponto só, ou um dia só: meio dia para cada lado

  const valores = serie.map((p) => p.valor);
  let lo = Math.min(...valores, ...(referencia ? [referencia.min] : []));
  let hi = Math.max(...valores, ...(referencia ? [referencia.max] : []));
  if (hi === lo) { hi += 1; lo -= 1; }
  const folga = (hi - lo) * 0.08;
  lo -= folga; hi += folga;

  const X = (t: number) => x0 + ((t - t0) / (t1 - t0)) * (x1 - x0);
  const Y = (v: number) => y1 - ((v - lo) / (hi - lo)) * (y1 - y0);

  const faixa = referencia ? { y: Y(referencia.max), h: Y(referencia.min) - Y(referencia.max) } : null;
  const yTicks = referencia
    ? [{ y: Y(referencia.max), valor: referencia.max }, { y: Y(referencia.min), valor: referencia.min }]
    : [{ y: Y(Math.max(...valores)), valor: Math.max(...valores) }, { y: Y(Math.min(...valores)), valor: Math.min(...valores) }];

  // Primeira, última e — se o período passar de dez dias — a do meio.
  const xTicks = [{ x: X(t0), em: new Date(t0).toISOString() }, { x: X(t1), em: new Date(t1).toISOString() }];
  if (t1 - t0 > 10 * 86_400_000) xTicks.splice(1, 0, { x: X((t0 + t1) / 2), em: new Date((t0 + t1) / 2).toISOString() });

  return {
    pontos: serie.map((p) => ({ x: X(Date.parse(p.em)), y: Y(p.valor) })),
    faixa, yTicks, xTicks,
    marcas: marcas.map((m) => { const t = Date.parse(m.em); return t >= t0 && t <= t1 ? X(t) : null; }),
    area,
  };
}
